import { Controller } from "@hotwired/stimulus"
import { confirmDialog } from "../dialogs/confirm"
import { domainMuteConfirm, hashtagMuteConfirm } from "../dialogs/mute_copy"

// Floating action menu for links and hashtags inside rendered item bodies.
// Attached to <body>: handles clicks on a[data-link-actions] / a[data-hashtag-actions]
// anywhere on the page via event delegation, since body HTML is sanitized and
// cannot carry its own controllers.
export default class extends Controller {
  connect() {
    this.beforeCache = () => this.#close()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.#close()
  }

  handle(event) {
    const link = event.target.closest("a[data-link-actions]")
    const hashtag = event.target.closest("a[data-hashtag-actions]")

    if (!link && !hashtag) {
      this.#close()
      return
    }
    // Modifier clicks keep native behavior (open in new tab etc.)
    if (link && (event.metaKey || event.ctrlKey || event.shiftKey)) return

    event.preventDefault()
    this.#close()
    if (link) {
      this.#showLinkMenu(link)
    } else {
      this.#showHashtagMenu(hashtag)
    }
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.#close()
  }

  #showLinkMenu(anchor) {
    const url = anchor.getAttribute("href")
    const domain = anchor.dataset.domain
    const menu = this.#buildMenu()
    menu.appendChild(this.#header(url))
    menu.appendChild(this.#item("bi-box-arrow-up-right", "Open link", () => {
      window.open(url, "_blank", "noopener")
    }))
    menu.appendChild(this.#item("bi-clipboard", "Copy link", () => {
      navigator.clipboard.writeText(url)
      window.toast("Link copied", { kind: "success" })
    }))
    if (domain) {
      menu.appendChild(this.#item("bi-bell-slash", `Mute all mentions linking to ${domain}`, async () => {
        this.#close()
        if (await confirmDialog(domainMuteConfirm(domain))) this.#post("/domains/mutes", { name: domain })
      }, "text-red-700 hover:bg-red-50"))
    }
    this.#position(anchor)
  }

  #showHashtagMenu(anchor) {
    const name = anchor.dataset.hashtagName
    const platform = anchor.dataset.platform
    const menu = this.#buildMenu()
    menu.appendChild(this.#header(`#${name}`))
    menu.appendChild(this.#item("bi-bell-slash", `Mute all mentions with #${name}`, async () => {
      this.#close()
      if (await confirmDialog(hashtagMuteConfirm(name))) this.#post("/hashtags/mutes", { name, platform })
    }, "text-red-700 hover:bg-red-50"))
    this.#position(anchor)
  }

  #buildMenu() {
    const menu = document.createElement("div")
    menu.className = "absolute z-50 w-max max-w-[calc(100vw-1rem)] bg-white border border-line rounded-lg shadow-float overflow-hidden text-[15px] font-medium p-1.5"
    document.body.appendChild(menu)
    this.menu = menu
    return menu
  }

  // Clamp on-screen using the real width, measured after the items are appended.
  #position(anchor) {
    const rect = anchor.getBoundingClientRect()
    const margin = 8
    const maxLeft = window.scrollX + document.documentElement.clientWidth - this.menu.offsetWidth - margin
    this.menu.style.left = `${Math.max(window.scrollX + margin, Math.min(rect.left + window.scrollX, maxLeft))}px`
    this.menu.style.top = `${rect.bottom + window.scrollY + 6}px`
  }

  #header(text) {
    const header = document.createElement("div")
    header.className = "px-3 py-2 border-b border-line text-meta text-ink-slate truncate max-w-[24rem]"
    header.textContent = text
    return header
  }

  #item(icon, label, onClick, extraClasses = "text-ink hover:bg-well rounded-md") {
    const button = document.createElement("button")
    button.type = "button"
    button.className = `w-full flex items-center gap-2.5 px-3 py-2 text-left whitespace-nowrap cursor-pointer ${extraClasses}`
    const i = document.createElement("i")
    i.className = `bi ${icon} text-ink-slate`
    i.setAttribute("aria-hidden", "true")
    const span = document.createElement("span")
    span.textContent = label
    button.append(i, span)
    button.addEventListener("click", onClick)
    return button
  }

  // Submits as a regular form so the server redirects back with a flash toast
  // and the feed re-renders without the muted content.
  #post(action, params) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = action
    form.hidden = true
    const add = (name, value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.appendChild(input)
    }
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (token) add("authenticity_token", token)
    Object.entries(params).forEach(([name, value]) => add(name, value))
    document.body.appendChild(form)
    form.requestSubmit()
  }

  #close() {
    this.menu?.remove()
    this.menu = null
  }
}
